import React, { RefObject, useCallback, useEffect, useRef } from "react";
import { Updater, useImmer } from "use-immer";

import 'rsuite/Toggle/styles/index.css';
import { standardClassNames } from "./calendar/utils";
import { AvailabilityTimeslotData, AvailabilityType, CronType, newAvailabilityTimeslot } from "./availiability/utils";
import { SlotGrid } from "./availiability/SlotGrid";
import { fromCron, toCron } from "./cron/utils";
import { toast } from "react-toastify";
import { useDialog } from "./Dialog";
import { Empty } from "../utils";
import { Editor } from "./availiability/Editor";
import { SlotKey } from "./calendar/utils/types";

export const classNames : typeof standardClassNames = {
  ...standardClassNames,
  container: "select-none",
  slot: ({isHovered, isGutter, isSelected} = {}) => 
    `w-12.5 h-8 border border-gray-200 ${isSelected && 'bg-blue-200'} ${isHovered && !isSelected && 'bg-gray-100'} ${isGutter && 'p-1 text-center'}`,
  item: ({isHovered = false} = {}) => 
    `absolute w-full h-full border border-gray-300 rounded cursor-pointer z-10 overflow-hidden ${isHovered && "bg-blue-200"}`,
}

export type ScheduleData = {
  id: string,
  type: string,
  timeslot: {
    schedule: {
      cron: string,
      duration: string,
    }
  }
}

const BACKGROUND_COLORS = {
  "Event::Availability": "bg-green-100",
  "Event::Unavailability": "bg-red-100",
  default: "bg-blue-100",
};

function backgroundColor(type: string) {
  return BACKGROUND_COLORS[type] || BACKGROUND_COLORS.default;
}

const initializeData = (scheduleData: ScheduleData[]): AvailabilityTimeslotData[] => {
  const data: AvailabilityTimeslotData[] = [];
  scheduleData.map((item) => {
    try {
      data.push({...item,cronData: fromCron(item.timeslot.schedule.cron, parseInt(item.timeslot.schedule.duration))})
    } catch (error) {
      console.error(`Failed to parse cron string: ${item.timeslot.schedule.cron}`, error);
    }
  });
  return data
};

const FormInputs: React.FC<{
  data: AvailabilityTimeslotData;
}> = ({ data }) => {
  const cronStr = toCron(data.cronData)
  if(data.type === 'new') return <></>;
  function isNew(id: string | number) { return typeof id === 'string' }
  return (<div>
      {!isNew(data.id) && <input readOnly type="text" name={`availabilities[${data.id}][id]`} value={data.id} /> }
      { data._destroy && <input readOnly type="text" name={`availabilities[${data.id}][_destroy]`} value="1" />}
      {/* <input type="text" name={`availabilities[${data.id}][timeslot_id]`} value={data.timeslot_id}/> */}
      <input readOnly type="text" name={`availabilities[${data.id}][type]`} value={data.type}/>
      <input readOnly type="text" name={`availabilities[${data.id}][timeslot_attributes][schedule][duration]`} value={data.timeslot.schedule.duration}/>
      <input readOnly type="text" name={`availabilities[${data.id}][timeslot_attributes][schedule][cron]`} value={cronStr}/>
    </div>
  );
};

const useRequestSubmit = (ref: RefObject<HTMLDivElement | null>) => {
  const requestSubmit = useCallback(() => {
    const form = ref.current?.closest("form");
    if(form) {
      ref.current?.closest('form')?.dispatchEvent(new Event('requestSubmit'))
      toast("saving...", {autoClose: 1000, hideProgressBar: true, toastId: 'saving', })
    } else {
      toast.error("No form found: you will lose your changes no matter what!")
    }
    window.onbeforeunload = null
  }, [ref]);

  const warnIfNoForm = useCallback(() => {
    if(!ref.current?.closest("form")) {
      toast.error("No form found: cannot save data!")
    }
  }, [ref]);

  return { requestSubmit, warnIfNoForm }
}

const clearWindowBeforeUnload = () => () => { window.onbeforeunload = null }

const setSaveTimeout = (triggerSave: () => void, delay: number) => {
  window.onbeforeunload = function() { return true };
  const timeoutId = setTimeout(triggerSave, 1000*2);
  return () => clearTimeout(timeoutId);
}

const saveEdits = (
  setData: Updater<AvailabilityTimeslotData[]>, 
  dataRef: RefObject<AvailabilityTimeslotData | null>
) => () => {
  setData((draft) => {
    if(!dataRef.current) { return }
    const idx = draft.findIndex((item) => item.id === dataRef.current?.id);
    if (idx !== -1) {
      draft[idx] = dataRef.current;
    }
  });
}

/*
Note: The data initializion / reloading / saving here is not ideal,
but it's working and encapsulated (I think!), so move on with life.
(Revisit when you need more instances of this pattern)

Here's the needs:
1. When the data is changed, it needs to be be saved to the server
2. When the server pushes an update (say, after a save), the data needs to be reloaded
    *but* needs to *not* trigger another save

The "Better Way" is probably something like:
const updateData: Updater<AvailabilityTimeslotData[]> = (update) => {
  setData((draft) => {
    update(draft);
    saveData();
  });
}

but this runs into some odd issues
*/
export const Availability: React.FC<{
  slotSize?: number;
  start?: Date;
  scheduleData?: ScheduleData[];
  hideInputs?: boolean
}> = ({
  slotSize = 60,
  start = new Date(),
  scheduleData = [],
  hideInputs = true,
}) => {
  const [ data, setData ] = useImmer(initializeData(scheduleData));

  const { Dialog, openDialog, isDialogOpen } = useDialog();
  const availabilityDataRef = useRef<AvailabilityTimeslotData | null>(null)

  const reloading = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const [ isLoading, setLoading ] = useImmer(true);
  const { requestSubmit, warnIfNoForm } = useRequestSubmit(ref);
  useEffect(() => setLoading(false), []);
  useEffect(clearWindowBeforeUnload, []);
  useEffect(warnIfNoForm, [ref.current])

  useEffect(() => {
    if(isLoading) {return}
    reloading.current = true
    setData(initializeData(scheduleData));
  }, [ scheduleData ])

  useEffect(() => {
    if(isLoading || isDialogOpen) {return}
    if(!reloading.current) { return setSaveTimeout(requestSubmit, 1000*2) }
    reloading.current = false
  }, [ data, reloading, isDialogOpen ]);

  const handleOpen = (itemData) => () => {
    if(!itemData) { return }
    availabilityDataRef.current = itemData
    openDialog();
  }

  const addTimeslot = 
    (kind: CronType, type: AvailabilityType) =>
    (start: SlotKey, end: SlotKey) => {
      setData((draft) => {
        draft.push(newAvailabilityTimeslot(
          new Date(start),
          new Date(end),
          (kind === "monthly" ? 1440 : slotSize),
          kind,
          type,
          "America/Los_Angeles", // TODO: Make timezone configurable
        ));
      });
  }

  const AvailabilityItem = ({
    itemId,
  }) => {
    const itemData = data.find((d) => d.id === itemId)

    if(!itemData) { return <></> }

    const onClick = useCallback(handleOpen(itemData), [itemData]);
    const bgColor = backgroundColor(itemData.type);
    const border = itemData._destroy ? "rounded border-4 border-red-200" : "";

    return <>
      <div
        className={`w-full h-full p-1 flex flex-col justify-between ${bgColor} ${border}`} 
        onClick={onClick}
      ></div>
    </>
  }

  return (<>
    <div ref={ref} className={hideInputs ? "hidden" : ""}>
      { data.map((item) => <FormInputs key={item.id} data={item}/>) }
    </div>

    <div className={classNames.container}>
      <SlotGrid slotSize={60} data={data} ItemContents={AvailabilityItem} addTimeslot={addTimeslot} />
      <Dialog
        onCancel={saveEdits(setData, availabilityDataRef)}
        CancelButton={Empty}
        ConfirmButton={Empty}
      >
        <Editor availabilityDataRef={availabilityDataRef} />
      </Dialog>
    </div>
  </>);
}

export default Availability;